export const sendToPatientOptions = [
  {
    title: 'Good faith estimate',
    caption: 'Includes Good Faith Estimate compliance language required by the No Surprises Act.',
    value: 'good-faith-estimate',
    details: `The GFE Patient Option Letter is a proprietary, copyrighted artifact designed to evoke transparency and tap into the psychology of patient payment — framing payment or a payment arrangement before treatment as the expected, normal part of care. It's built to reduce post-service A/R, increase collection rates at the point of care, and give patients a clear, dignified path forward when they need accommodations.`
  },
  {
    title: 'Patient option letter',
    caption: 'Streamlined letter with payment options only — no GFE compliance disclaimers.',
    value: 'patient-option-letter',
    details: `The Patient Option Letter is a proprietary, copyrighted artifact designed to evoke transparency and tap into the psychology of patient payment — framing payment or a payment arrangement before treatment as the expected, normal part of care. It's built to reduce post-service A/R, increase collection rates at the point of care, and give patients a clear, dignified path forward when they need accommodations.`
  }
];

export const paymentTypes = [
  { value: 'in-person', title: 'In-person', icon: 'users-mono' },
  { value: 'remote', title: 'Remote', icon: 'video-camera-mono' }
];

export const ASSURANCE_RATING_MAX = 95;
export const ASSURANCE_RATING_MIN = 0;
export const DEFAULT_ASSURANCE_RATING = 80;

export const PAYMENT_METHOD_FIELDS = {
  CASH: 'cash',
  CHECK: 'check',
  CREDIT_DEBIT_CARD: 'credit-card',
  ACH: 'ach',
  PRE_AUTH: 'pre-auth',
  RECURRING_CHARGE: 'recurring-charge',
  DEFER_PAYMENT: 'defer-payment'
};

export const RECURRING_CHARGE_FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' }
];

export const RECURRING_CHARGE_DURATIONS = [
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 9, label: '9 months' },
  { value: 12, label: '12 months' },
  { value: 18, label: '18 months' },
  { value: 24, label: '24 months' }
];

export const DEFAULT_RECURRING_CHARGE_CONFIG = {
  require_approval: true,
  amount: 1,
  frequency: 'monthly',
  duration_months: 6,
  offer_on_kiosk: false
};

export const PHASE_FIELDS = {
  IN_NETWORK: 'in_network',
  OUT_OF_NETWORK: 'out_of_network',
  SELF_PAY: 'self_pay'
};

export const paymentMethods = [
  {
    id: 1,
    field: PAYMENT_METHOD_FIELDS.CASH,
    icon: 'cash-mono',
    title: 'Cash',
    description: 'Accept cash at the front desk or via office safe drop.',
    allowed: ['in-person'],
    recommended: false
  },
  {
    id: 2,
    field: PAYMENT_METHOD_FIELDS.CHECK,
    icon: 'pay-by-check-mono',
    title: 'Check',
    description: 'Accept paper checks endorsed to the practice.',
    allowed: ['in-person'],
    recommended: false
  },
  {
    id: 3,
    field: PAYMENT_METHOD_FIELDS.CREDIT_DEBIT_CARD,
    icon: 'credit-card-mono',
    title: 'Credit/debit card',
    description: 'Accept major cards, HSA, and FSA cards.',
    allowed: ['in-person', 'remote'],
    recommended: false
  },
  {
    id: 4,
    field: PAYMENT_METHOD_FIELDS.ACH,
    icon: 'building-mono',
    title: 'E-check / ACH',
    description: 'Pay directly from a bank account via ACH.',
    allowed: ['in-person', 'remote'],
    recommended: false
  },
  {
    id: 5,
    field: PAYMENT_METHOD_FIELDS.PRE_AUTH,
    icon: 'lock-2-mono',
    title: 'Guarantee future balance (COF)',
    description: 'Patient places a card on file that secures the estimated balance.',
    allowed: ['in-person', 'remote'],
    recommended: false
  },
  {
    id: 6,
    field: PAYMENT_METHOD_FIELDS.RECURRING_CHARGE,
    icon: 'recurring-charges-mono',
    title: 'Recurring charge',
    description: 'Patient enrolls in an in-house payment plan.',
    allowed: ['in-person', 'remote'],
    recommended: false
  },
  {
    id: 7,
    field: PAYMENT_METHOD_FIELDS.DEFER_PAYMENT,
    icon: 'handshake-mono',
    title: 'Defer payment',
    description:
      'Drives a conversation for patients who need accommodations before treatment is started. Your team will be alerted so they can review options before the visit proceeds.',
    allowed: ['in-person', 'remote'],
    recommended: true
  }
];

const ALLOWED_CHANNELS_BY_FIELD = paymentMethods.reduce((acc, method) => {
  acc[method.field] = new Set(method.allowed);
  return acc;
}, {});

const normalizeMethodChannels = (method) => {
  const allowed = ALLOWED_CHANNELS_BY_FIELD[method.field];
  if (!allowed) return method;
  return {
    ...method,
    in_person: allowed.has('in-person') ? !!method.in_person : false,
    remote: allowed.has('remote') ? !!method.remote : false
  };
};

export const DEFAULT_PAYMENT_SETUP = {
  letter_type: 'good-faith-estimate',
  assurance_thresholds: {
    show_patient_rating: DEFAULT_ASSURANCE_RATING,
    show_on_letter: true
  },
  payment_methods: [
    { field: PAYMENT_METHOD_FIELDS.CASH, in_person: true, remote: false },
    { field: PAYMENT_METHOD_FIELDS.CHECK, in_person: true, remote: false },
    { field: PAYMENT_METHOD_FIELDS.CREDIT_DEBIT_CARD, in_person: true, remote: true },
    { field: PAYMENT_METHOD_FIELDS.ACH, in_person: true, remote: true },
    { field: PAYMENT_METHOD_FIELDS.PRE_AUTH, in_person: true, remote: true },
    {
      field: PAYMENT_METHOD_FIELDS.RECURRING_CHARGE,
      in_person: true,
      remote: true,
      config: DEFAULT_RECURRING_CHARGE_CONFIG
    },
    { field: PAYMENT_METHOD_FIELDS.DEFER_PAYMENT, in_person: true, remote: true }
  ],
  phases: [
    {
      field: PHASE_FIELDS.IN_NETWORK,
      card_on_file: { route_to: 'patient-option' },
      authorization_applied: { route_to: 'all-visits-this-year' },
      charge_limit: {
        route_to: 'combined-allowable-amount',
        config: { percent_above: 10, dollar_cap: 0 }
      }
    },
    {
      field: PHASE_FIELDS.OUT_OF_NETWORK,
      card_on_file: { route_to: 'patient-option' },
      authorization_applied: { route_to: 'all-visits-this-year' },
      billing_method: { route_to: 'eligibility-calculation', config: {} },
      patient_portion_cpt: { route_to: 'self-pay-amount' },
      balance_billing: { enabled: false }
    },
    {
      field: PHASE_FIELDS.SELF_PAY,
      card_on_file: { route_to: 'patient-option' },
      authorization_applied: { route_to: 'all-visits-this-year' },
      charge_limit: { route_to: 'guarantee-full-cost', config: { dollar_cap: 0 } }
    }
  ]
};

export const hydratePaymentSetup = (incoming) => {
  const base = DEFAULT_PAYMENT_SETUP;
  if (!incoming) {
    return { ...base, payment_methods: base.payment_methods.map(normalizeMethodChannels) };
  }

  const defaultByField = new Map(base.payment_methods.map((m) => [m.field, m]));
  const incomingMethods = incoming.payment_methods;
  const mergedMethods = Array.isArray(incomingMethods)
    ? incomingMethods.map((incomingMethod) => {
        const defaultMethod = defaultByField.get(incomingMethod.field);
        const merged = !defaultMethod
          ? incomingMethod
          : {
              ...defaultMethod,
              ...incomingMethod,
              ...(defaultMethod.config && {
                config: { ...defaultMethod.config, ...(incomingMethod.config ?? {}) }
              })
            };
        return normalizeMethodChannels(merged);
      })
    : base.payment_methods.map(normalizeMethodChannels);

  const incomingPhaseByField = new Map((incoming.phases ?? []).map((p) => [p.field, p]));
  const mergedPhases = base.phases.map((defaultPhase) => ({
    ...defaultPhase,
    ...(incomingPhaseByField.get(defaultPhase.field) ?? {})
  }));

  return {
    ...base,
    ...incoming,
    assurance_thresholds: {
      ...base.assurance_thresholds,
      ...(incoming.assurance_thresholds ?? {})
    },
    payment_methods: mergedMethods,
    phases: mergedPhases
  };
};
